import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { StepsEnum } from '../../../Interfaces/enums';
import { environment } from '../../../../environments/environment';
import { OnboardingFacade } from '../../../Facades/onboarding.facade';
import { AvContentContainerComponent } from '../../Shared/Components/av-content-container/av-content-container.component';
import { AvLanguageDropdownComponent } from '../../Shared/Components/av-language-dropdown/av-language-dropdown.component';
import { AvThemeToggleComponent } from '../../Shared/Components/theme-toggle/theme-toggle.component';
import { SetupStep } from '../Interfaces/setup-step.interface';
import { StepStatus } from '../Types/step-status.type';

@Component({
  selector: 'app-setup-container',
  standalone: true,
  imports: [
    RouterOutlet,
    MatProgressSpinnerModule,
    MatIconModule,
    TranslateModule,
    AvContentContainerComponent,
    AvLanguageDropdownComponent,
    AvThemeToggleComponent,
  ],
  templateUrl: './setup-container.component.html',
  styleUrls: ['./setup-container.component.scss'],
})
export class SetupContainerComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private configService = inject(OnboardingFacade);
  private destroy$ = new Subject<void>();

  currentUrl: string = '';
  configLoaded = false;
  version = environment.version;

  steps: SetupStep[] = [
    {
      id: '1',
      name: StepsEnum.COMPANY,
      label: 'onboarding.setup.stepper.steps.company',
      route: 'company-setup',
      icon: 'business'
    },
    {
      id: '2',
      name: StepsEnum.PLAN,
      label: 'onboarding.setup.stepper.steps.plan',
      route: 'plan-selection',
      icon: 'credit_card'
    },
    {
      id: '3',
      name: StepsEnum.LICENCES,
      label: 'onboarding.setup.stepper.steps.licences',
      route: 'licences',
      icon: 'confirmation_number'
    },
    {
      id: '4',
      name: StepsEnum.INTUNE,
      label: 'onboarding.setup.stepper.steps.intune',
      route: 'connect-intune',
      icon: 'cloud_sync'
    },
    {
      id: '5',
      name: StepsEnum.REVIEW,
      label: 'onboarding.setup.stepper.steps.review',
      route: 'review',
      icon: 'fact_check'
    }
  ];

  ngOnInit() {
    this.trackCurrentRoute();
    this.loadConfiguration();
  }

  trackCurrentRoute() {
    this.currentUrl = this.router.url;
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.urlAfterRedirects;
    });
  }

  isStepActive(step: SetupStep): boolean {
    return this.currentUrl.includes(step.route);
  }

  private activeIndex(): number {
    return this.steps.findIndex((step) => this.currentUrl.includes(step.route));
  }

  stepStatus(index: number): StepStatus {
    const active = this.activeIndex();
    if (active === -1) return 'pending';
    if (index < active) return 'done';
    if (index === active) return 'active';
    return 'pending';
  }

  loadConfiguration(): void {
    if (this.configService.configLoaded()) {
      this.configLoaded = true;
      return;
    }

    this.configService.getOnboardingConfig({ skipLoading: true }).subscribe({
      next: () => {
        this.configLoaded = true;
      },
      error: () => {
        this.configLoaded = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
